/* <gallery-lightbox> — fullscreen <dialog> image viewer in <media-gallery>;
   rebuilt per open from visible image slides. Contract: product-gallery. */

const root = document.documentElement;
const imageOf = (s) => (s.querySelector('video-toggle') ? null : s.querySelector('img'));
/* #415: a deferred slide image (data-src, no src — see media-gallery.js) is
   swapped in before it is cloned into the dialog, so the clone has a source. */
const hydrate = (img) => {
  if (!img.dataset.src) return;
  if (img.dataset.srcset) img.srcset = img.dataset.srcset;
  img.src = img.dataset.src;
  delete img.dataset.src;
  delete img.dataset.srcset;
};

class GalleryLightbox extends HTMLElement {
  connectedCallback() {
    const dialog = (this.dialog = this.querySelector('dialog'));
    this.track = this.querySelector('ul');
    const gallery = this.closest('media-gallery');
    this.slides = gallery?.querySelector('[data-slider-track]');
    if (!dialog || !this.track || !this.slides) return;
    gallery.tabIndex = -1;
    /* M25 / #543 Fix 13 (2026-09-23): a tap anywhere on an image slide opens the viewer, and so does the
       slide's own <button> (keyboard and screen readers; snippets/product-gallery.liquid), whose native
       Enter/Space click lands here too — so the slides need no key handling of their own. The slide itself
       is no longer role="button": Meta's automatic click tracking treated every photo tap as a button click
       and ran on it (the tap 288-296 → 160-176 ms at 4x CPU without it). */
    const openFrom = (e) => {
      const s = e.target.closest('[data-media-id]');
      if (s && imageOf(s)) {
        e.preventDefault();
        this.open(s.dataset.mediaId);
      }
    };
    this.slides.addEventListener('click', openFrom);
    dialog.addEventListener('click', (e) => {
      const b = e.target.closest('button');
      if (b) b.dataset.step ? this.step(+b.dataset.step) : dialog.close();
      else if (!e.target.closest('img')) dialog.close();
    });
    dialog.addEventListener('keydown', (e) => this.step({ ArrowLeft: -1, ArrowRight: 1 }[e.key]));
    dialog.addEventListener('close', () => {
      root.style.overflow = '';
      (gallery.querySelector('[aria-current="true"]') || gallery).focus();
    });
  }

  open(mediaId) {
    const track = this.track;
    const items = [];
    let at = 0;
    for (const s of this.slides.children) {
      const img = s.hidden ? null : imageOf(s);
      if (!img) continue;
      hydrate(img);
      if (s.dataset.mediaId === mediaId) at = items.length;
      const li = Object.assign(document.createElement('li'), { className: track.dataset.itemClass });
      li.append(Object.assign(img.cloneNode(), { className: track.dataset.imgClass, loading: 'lazy', sizes: '100vw' }));
      items.push(li);
    }
    if (!items.length) return;
    track.replaceChildren(...items);
    root.style.overflow = 'hidden';
    this.dialog.showModal();
    if (at) track.scrollLeft = at * track.clientWidth;
  }

  step(dir) {
    if (dir) this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: 'smooth' });
  }
}

if (!customElements.get('gallery-lightbox')) customElements.define('gallery-lightbox', GalleryLightbox);
